import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import DraggableCollapsibleItem from 'components/DraggableCollapsibleItem'
import { Container, IconButton, Inline, Stack } from '@airbus/components-react/components'
import PageHeader from 'components/pageHeader'
import { useNavigate, useParams } from 'react-router-dom'
import WidgetAircrafts from './components/widgets/WidgetAircrafts/widgetAircrafts'
import WidgetHIL from './components/widgets/WidgetHIL/widgetHIL'
import WidgetPPPR from './components/widgets/WidgetPPPR/widgetPPPR'
import WidgetOE from './components/widgets/WidgetOE/widgetOE'
import Verdict from './components/widgets/components/verdict'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesDown, faAnglesUp } from '@fortawesome/free-solid-svg-icons'

const {
  TABFLOT_STORE_NAME,
  TABFLOT_WDG_AERONEFS_UNAME,
  TABFLOT_WDG_HIL_UNAME,
  TABFLOT_WDG_PPPR_UNAME,
  TABFLOT_WDG_OE_UNAME,
} = ComponentsUNames

const FleetPanWidgets = ({ id, pageUniqueName, selectedAircraft, handleAircraftChange, aircrafts, setAircrafts }) => {
  let Component = null
  switch (id) {
    case TABFLOT_WDG_AERONEFS_UNAME:
      Component = WidgetAircrafts
      break
    case TABFLOT_WDG_HIL_UNAME:
      Component = WidgetHIL
      break
    case TABFLOT_WDG_PPPR_UNAME:
      Component = WidgetPPPR
      break
    case TABFLOT_WDG_OE_UNAME:
      Component = WidgetOE
      break
  }

  return (
    <Component
      pageUniqueName={pageUniqueName}
      selectedAircraft={selectedAircraft}
      handleAircraftChange={handleAircraftChange}
      aircrafts={aircrafts}
      setAircrafts={setAircrafts}
    />
  )
}

const getVerdict = (id, aircraft) => {
  if (!aircraft) return null
  switch (id) {
    case TABFLOT_WDG_HIL_UNAME:
      return aircraft.hil
    case TABFLOT_WDG_PPPR_UNAME:
      return aircraft.pppr
    case TABFLOT_WDG_OE_UNAME:
      return aircraft.workorder
  }

  return
}

const ActionButtons = ({ isAllExpanded, onExpandAll }) => {
  const { t } = useTranslation()

  return (
    <IconButton
      variant='ghost'
      size='xsmall'
      onClick={onExpandAll}
      title={isAllExpanded ? t('collapse_all') : t('expand_all')}
    >
      <FontAwesomeIcon icon={isAllExpanded ? faAnglesUp : faAnglesDown} />
    </IconButton>
  )
}

const FleetPan = () => {
  const { t } = useTranslation()
  const pageUniqueName = TABFLOT_STORE_NAME

  const [aircrafts, setAircrafts] = useState()

  const hasPpprPermissions = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.TABFLOT_PPPR_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const [items, setItems] = useState([
    {
      id: TABFLOT_WDG_AERONEFS_UNAME,
      expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_AERONEFS_UNAME, pageUniqueName),
      titleKey: '9500051',
      isLocked: true,
    },
    ...(hasPpprPermissions
      ? [
          {
            id: TABFLOT_WDG_HIL_UNAME,
            expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_HIL_UNAME, pageUniqueName),
            titleKey: '9500052',
            defaultIndex: 1,
          },
          {
            id: TABFLOT_WDG_PPPR_UNAME,
            expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_PPPR_UNAME, pageUniqueName),
            titleKey: '9500053',
            defaultIndex: 2,
          },
          {
            id: TABFLOT_WDG_OE_UNAME,
            expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_OE_UNAME, pageUniqueName),
            titleKey: '9500054',
            defaultIndex: 3,
          },
        ]
      : [
          {
            id: TABFLOT_WDG_HIL_UNAME,
            expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_HIL_UNAME, pageUniqueName),
            titleKey: '9500052',
            defaultIndex: 1,
          },
          {
            id: TABFLOT_WDG_OE_UNAME,
            expanded: LocalStorage.getStoredExpanded(TABFLOT_WDG_OE_UNAME, pageUniqueName),
            titleKey: '9500054',
            defaultIndex: 2,
          },
        ]
    ).sort(
      (a, b) =>
        LocalStorage.getStoredIndex(a.id, a.defaultIndex, pageUniqueName) -
        LocalStorage.getStoredIndex(b.id, b.defaultIndex, pageUniqueName),
    ),
  ])

  const moveItem = useCallback(
    (fromIndex, toIndex) => {
      setItems((item) => {
        const updatedItems = [...item]
        const [movedItem] = updatedItems.splice(fromIndex, 1)
        updatedItems.splice(toIndex, 0, movedItem)
        // Sync positions with local storage
        updatedItems.forEach((item, i) => {
          const propToUpdate = [LocalStorage.KEY_PREFS, pageUniqueName, item.id + '|POSITION']
          LocalStorage.put(propToUpdate, i)
        })
        return updatedItems
      })
    },
    [items],
  )

  const handleExpandedChange = (id, expanded) => {
    // Sync with local storage
    const propToUpdate = [LocalStorage.KEY_PREFS, pageUniqueName, id + '|EXPANDED']
    LocalStorage.put(propToUpdate, expanded)
    setItems(items.map((item) => (item.id === id ? { ...item, expanded } : item)))
  }

  const handleExpandAll = () => {
    const allExpanded = items.every((item) => item.expanded)

    // Sync with local storage
    items.forEach(({ id }) =>
      LocalStorage.put([LocalStorage.KEY_PREFS, pageUniqueName, id + '|EXPANDED'], !allExpanded),
    )
    setItems(items.map((item) => ({ ...item, expanded: !allExpanded })))
  }

  const { avimmatr: sa } = useParams()
  // We replaced dot char by it's hex value because webserver is ignoring routes with dots
  const selectedAircraft = sa && decodeURIComponent(sa)

  const navigate = useNavigate()
  const baseUrl = process.env.PUBLIC_URL

  const handleAircraftChange = (ac) => {
    navigate(baseUrl + '/fleetpan/' + encodeURIComponent(ac), {
      preventScrollReset: true,
      replace: true,
    })
  }

  const [hoverIndex, setHoverIndex] = useState(null)
  const [dropPosition, setDropPosition] = useState(null)

  let navItems = [
    {
      title: t('9500050'),
      url: baseUrl + '/fleetpan',
    },
  ]

  if (selectedAircraft) {
    navItems.push({
      title: selectedAircraft,
      url: baseUrl + `/fleetpan/${selectedAircraft}`,
    })
  }

  return (
    <>
      <PageHeader
        pageTitle={t('9500050')}
        navItems={navItems}
        ActionButtons={() => (
          <ActionButtons isAllExpanded={items.every((item) => item.expanded)} onExpandAll={handleExpandAll} />
        )}
      />
      <Container style={{ flex: 1 }} until='xxl'>
        <Stack spacing='2-x'>
          {items?.map(({ id, expanded, isLocked, titleKey }, index) => (
            <DraggableCollapsibleItem
              key={id}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
              dropPosition={dropPosition}
              setDropPosition={setDropPosition}
              index={index}
              moveListItem={moveItem}
              isLocked={isLocked}
              title={
                <Inline spacing='1-x' alignY='center'>
                  <Verdict
                    value={getVerdict(
                      id,
                      aircrafts?.find((a) => a.avimmatr === selectedAircraft),
                    )}
                  />
                  <span>{t(titleKey)}</span>
                </Inline>
              }
              expanded={expanded}
              onExpandedChange={(exp) => handleExpandedChange(id, exp)}
            >
              <FleetPanWidgets
                id={id}
                pageUniqueName={pageUniqueName}
                selectedAircraft={selectedAircraft}
                handleAircraftChange={handleAircraftChange}
                aircrafts={aircrafts}
                setAircrafts={setAircrafts}
              />
            </DraggableCollapsibleItem>
          ))}
        </Stack>
      </Container>
    </>
  )
}

export default FleetPan
